import { RingFinishEnum } from '@/app/enums/RingFinishEnum';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';

import type {
  Gen3RingWizardData,
  WizardCarouselImage,
  WizardCarouselImageContent,
} from './types';

const HERITAGE_SILVER_GRADIENT_IMG =
  'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-heritage-silver-gradient.jpg';
const RING_SILVER_THUMB_IMG =
  'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-horizon-silver.png';
const HORIZON_STEALTH_IMG =
  'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-stealth@2x.png';
const HORIZON_TITANIUM_IMG =
  'https://s3.amazonaws.com/ouraring.com/images/product/wizard/product-image-horizon-titanium-tilt.png';

const APP_UI_CAROUSEL_IMG: WizardCarouselImage = {
  altMsgKey: 'pdp_carousel_alt_mobile_app',
  content: null,
  contentTitle: null,
  inverseText: true,
  mobileSrc: null,
  originalSrc:
    'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-ring-style-app-v2.jpg',
  originalSrcKey: 'pdp_slide_2_app_ui',
  thumbnailSrc: '',
};

const HERITAGE_LIFESTYLE_CAROUSEL_IMG: WizardCarouselImage = {
  altMsgKey: 'pdp_carousel_alt_woman_sunset',
  content: null,
  contentTitle: null,
  inverseText: true,
  mobileSrc: null,
  originalSrc:
    'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-heritage-silver-gradient.jpg',
  originalSrcKey: null,
  thumbnailSrc: '',
};

const HORIZON_LIFESTYLE_CAROUSEL_IMG: WizardCarouselImage = {
  altMsgKey: 'pdp_carousel_alt_well_dressed_man',
  content: null,
  contentTitle: null,
  inverseText: false,
  mobileSrc: null,
  originalSrc:
    'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-ring-horizon-lifestyle.jpg',
  originalSrcKey: null,
  thumbnailSrc: '',
};

const TECH_SPECS_CONTENT: WizardCarouselImageContent[] = [
  {
    copy: 'sensors_copy',
    heading: 'pdp_horizon_breakthrough_sensors_title',
    iconName: 'sensors_boxed',
  },
  {
    copy: 'seven_days_battery_copy',
    heading: 'pdp_highlights_battery_life',
    iconName: 'battery_boxed',
  },
  {
    copy: 'titanium_copy',
    heading: 'pdp_highlights_titanium',
    iconName: 'titanium_boxed',
  },
  {
    copy: 'water_resistant_copy',
    heading: 'pdp_highlights_water_resistant',
    iconName: 'water_resist_boxed',
  },
];

const TECH_SPECS_CAROUSEL_IMG: WizardCarouselImage = {
  altMsgKey: 'pdp_carousel_alt_ring_closeup',
  originalSrc:
    'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-ring-style-ring-closeup.png',
  thumbnailSrc:
    'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-ring-thumb-closeup.jpg',
  content: TECH_SPECS_CONTENT,
  contentFormat: 'OR3TechSpec',
  contentTitle: null,
  originalSrcKey: null,
  mobileSrc: null,
  inverseText: false,
};

export const gen3WizardData: Gen3RingWizardData = {
  [RingStyleEnum.Heritage]: {
    [RingFinishEnum.Black]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_heritage_black_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-black-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-black.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-heritage-silver.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HERITAGE_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_heritage',
          descMsgKey: 'pdp_style_description_heritage',
          swatch: {
            altMsgKey: 'pdp_ring_color_black_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-black@2x.png',
            slug: 'black',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-black@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-black@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-black@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-heritage-black.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.BrushedSilver]: null,
    [RingFinishEnum.Gold]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_heritage_gold_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-gold-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-gold.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-heritage-silver.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HERITAGE_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_heritage',
          descMsgKey: 'pdp_style_description_heritage',
          swatch: {
            altMsgKey: 'pdp_ring_color_gold_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-gold@2x.png',
            slug: 'gold',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-gold@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-gold@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-gold@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-heritage-gold.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Rose]: null,
    [RingFinishEnum.RoseGold]: null,
    [RingFinishEnum.Silver]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_heritage_silver_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-silver-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-silver.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-heritage-silver.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HERITAGE_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_heritage',
          descMsgKey: 'pdp_style_description_heritage',
          swatch: {
            altMsgKey: 'pdp_ring_color_silver_label',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-silver@2x.png',
            slug: 'silver',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-silver@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-silver@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-silver@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc: HERITAGE_SILVER_GRADIENT_IMG,
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Stealth]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_heritage_stealth_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-stealth-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-heritage-stealth.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-heritage-silver.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HERITAGE_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_heritage',
          descMsgKey: 'pdp_style_description_heritage',
          swatch: {
            altMsgKey: 'pdp_ring_color_stealth_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-stealth@2x.png',
            slug: 'stealth',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-stealth@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-stealth@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/heritage-stealth@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-heritage-stealth.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Titanium]: null,
  },
  [RingStyleEnum.Horizon]: {
    [RingFinishEnum.Black]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_black_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-black-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-black.mp4',
            thumbnailSrc: RING_SILVER_THUMB_IMG,
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'pdp_ring_color_black_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-black@2x.png',
            slug: 'black',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-black@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-black@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-black@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_black',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-black.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.BrushedSilver]: null,
    [RingFinishEnum.Gold]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_gold_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-gold-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-gold.mp4',
            thumbnailSrc: RING_SILVER_THUMB_IMG,
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'pdp_ring_color_gold_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-gold@2x.png',
            slug: 'gold',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-gold@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-gold@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-gold@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_gold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-gold-gradient.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Rose]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_rose_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-rose-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-rose.mp4',
            thumbnailSrc: RING_SILVER_THUMB_IMG,
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'pdp_ring_color_rose_gold_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-rose@2x.png',
            slug: 'rose gold',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-rose@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_rosegold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-rose@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_rosegold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-rose@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_rosegold',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-rose-gradient.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.RoseGold]: null,
    [RingFinishEnum.Silver]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_silver_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-silver-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-silver.mp4',
            thumbnailSrc: RING_SILVER_THUMB_IMG,
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'pdp_ring_color_silver_label',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-silver@2x.png',
            slug: 'silver',
          },
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-silver@2x.png',
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-silver@2x.png',
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/horizon-silver@2x.png',
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_silver',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-silver.jpg',
          inverseText: false,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Stealth]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_stealth_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-stealth-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-stealth.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-video-thumb-horizon-silver.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'pdp_ring_color_stealth_title',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-stealth@2x.png',
            slug: 'stealth',
          },
          originalSrc: HORIZON_STEALTH_IMG,
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc: HORIZON_STEALTH_IMG,
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc: HORIZON_STEALTH_IMG,
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_stealth',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-stealth.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
    [RingFinishEnum.Titanium]: {
      styleStep: {
        carouselImages: [
          {
            altMsgKey: 'pdp_carousel_alt_horizon_titanium_video',
            content: null,
            contentTitle: null,
            originalSrcKey: null,
            mobileSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-titanium-mobile.mp4',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-horizon-titanium.mp4',
            thumbnailSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-video-thumb-horizon-titanium.png',
            inverseText: false,
          },
          APP_UI_CAROUSEL_IMG,
          HORIZON_LIFESTYLE_CAROUSEL_IMG,
          TECH_SPECS_CAROUSEL_IMG,
        ],
        image: null,
        selector: {
          altMsgKey: 'pdp_style_horizon',
          descMsgKey: 'pdp_style_description_horizon',
          swatch: {
            altMsgKey: 'product_catalog_color_titanium',
            originalSrc:
              'https://s3.amazonaws.com/ouraring.com/images/product/simple/pdp-swatch-horizon-titanium.png',
            slug: 'brushed titanium',
          },
          originalSrc: HORIZON_TITANIUM_IMG,
        },
      },
      finishStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_titanium',
          originalSrc: HORIZON_TITANIUM_IMG,
          inverseText: false,
        },
        selector: {
          altMsgKey: 'product_catalog_color_titanium',
          originalSrc: HORIZON_TITANIUM_IMG,
        },
      },
      sizeStep: {
        carouselImages: null,
        image: {
          altMsgKey: 'product_catalog_color_titanium',
          originalSrc:
            'https://s3.amazonaws.com/ouraring.com/images/product/wizard/pdp-sizing-horizon-titanium.jpg',
          inverseText: true,
        },
        selector: null,
      },
    },
  },
};
